import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppUseSelector } from '../../redux/hooks'
import { POCKET_LIMIT, PocketWord } from '../../redux/reducers/pocketReducer'
import { extractDefinition } from 'utils/utils'
import { removeSpaceBetweenWords, wordSimplified } from 'utils/helperFunction'
import { hasUsedPocket, pocketWord, takeOut } from './pocketController'
import { plainMeaning, sliceSentence } from './pocketSentence'
import { PocketIcon } from './PocketIcon'
import { meaningSenses, wordMeaning } from '../../i18n/localizedContent'

interface PocketLinkProps {
  /** The reader's sentence for the tapped word, with its English (story page only; the pocket saves English). */
  sentence?: { text: string; en: string } | null
  /** Closes the word panel; on phones the bottom sheet would hide the pouch the word flies to. */
  closePanel?: () => void
}

/** A small "Pocket it" button under the word's level in the word panel. */
const PocketLink: React.FC<PocketLinkProps> = ({ sentence, closePanel }) => {
  const { t } = useTranslation('reader')
  const ref = useRef<HTMLDivElement>(null)
  // The icon pops only when the learner pockets the word here, not every time a pocketed word opens.
  const [popped, setPopped] = useState('')
  // "Take out" shows on hover only once the pointer has left the button that just pocketed the word.
  const [armed, setArmed] = useState(true)
  const { wordDetails, storyDictionary, currentStory } = AppUseSelector(({ story }) => story)
  const { words, pending, loaded } = AppUseSelector(({ pocket }) => pocket)
  const word = wordSimplified(wordDetails?.word || '')
  if (!word) return null

  const inPocket = words.some((w) => w.word === word) || pending.includes(word)
  const full = words.length + pending.length >= POCKET_LIMIT
  // Learners who have never pocketed a word may not know the pocket exists, so the button glows.
  const discover = loaded && !words.length && !pending.length && !hasUsedPocket()

  const onPocket = () => {
    setPopped(word)
    setArmed(false)
    const panel = ref.current?.closest('.word_detail_modal_main_div')
    const origin = panel?.querySelector('.big_word') || null
    const source = document.querySelector('.main_title_big_text.word-active')
    const sentenceWords = sliceSentence(wordDetails?.sentenceWords, word, sentence?.text)
    const sameSentence = !!sentence?.text && sentenceWords.join('').replace(/\s+/g, '') === sentence.text.replace(/\s+/g, '')
    // The pocket saves the English meaning; the learner-language one rides along locally so the
    // pocket shows it at once (the pocket API sends it on the next load).
    const { primary } = wordMeaning(wordDetails)
    const localized = primary.localized
      ? { localizedMeaning: meaningSenses(primary.text)[0] || primary.text, meaningLanguage: primary.lang }
      : {}
    pocketWord(
      {
        ...localized,
        word,
        languageDictionaryId: wordDetails?._id || storyDictionary?.[word]?._id || null,
        pinyin: removeSpaceBetweenWords(wordDetails?.pinyin || ''),
        meaning: plainMeaning(extractDefinition(wordDetails?.english || '')),
        hasAudio: (wordDetails?.has_audio ?? storyDictionary?.[word]?.has_audio) === true,
        sentenceWords,
        sentenceEn: sameSentence ? sentence?.en || '' : '',
        storyId: currentStory?.id || '',
      } as PocketWord,
      origin,
      source,
    )
    // The characters have already been measured, so the sheet can slide away under them.
    if (window.matchMedia?.('(max-width: 767px)').matches) setTimeout(() => closePanel?.(), 60)
  }

  return (
    <div ref={ref} className='pocket_toggle_row'>
      <button
        type='button'
        className={`pocket_toggle${inPocket ? ' on' : ''}${inPocket && popped === word ? ' pop' : ''}${armed ? ' armed' : ''}${discover ? ' discover' : ''}`}
        aria-pressed={inPocket}
        title={!inPocket && full ? t('pocket.fullHint') : undefined}
        onClick={() => (inPocket ? takeOut(word, 'remove') : onPocket())}
        onMouseLeave={() => setArmed(true)}
      >
        <PocketIcon filled={inPocket} />
        <span className='pt-label'>{inPocket ? t('pocket.inPocket') : t('pocket.pocketIt')}</span>
        {inPocket && <span className='pt-alt'>{t('pocket.takeOut')}</span>}
      </button>
    </div>
  )
}

export default PocketLink
